import React, { useState } from 'react';
import { 
  Briefcase, MapPin, Clock, ChevronDown, ArrowRight, 
  CheckCircle, Users, Lightbulb, Heart, Rocket
} from 'lucide-react';
import './Careers.css';

interface Job {
  id: string;
  title: string;
  department: string;
  location: string;
  type: string;
  responsibilities: string[];
  requirements: string[];
}

const Careers: React.FC = () => {
  const [expandedJob, setExpandedJob] = useState<string | null>(null);

  const benefits = [
    { icon: <Users size={32} />, title: 'Collaborative Teams', description: 'Work with experts across domains' },
    { icon: <Lightbulb size={32} />, title: 'Learning Opportunities', description: 'Continuous domain learning' },
    { icon: <Heart size={32} />, title: 'Work-Life Balance', description: 'Flexible working arrangements' },
    { icon: <Rocket size={32} />, title: 'Growth Path', description: 'Clear career progression' },
  ];

  const jobs: Job[] = [
    {
      id: 'product-analyst',
      title: 'Product Analyst — QMS',
      department: 'Product',
      location: 'Remote / Hybrid',
      type: 'Full-time',
      responsibilities: [
        'Gather User Requirements Specifications (URS) from stakeholders',
        'Translate requirements into detailed user stories',
        'Validate acceptance criteria with product team',
        'Work closely with development team during sprints',
        'Conduct user acceptance testing'
      ],
      requirements: [
        'Understanding of GxP processes or strong curiosity and ability to learn quickly',
        '3+ years experience in product analysis or business analysis',
        'Excellent communication and documentation skills',
        'Experience with agile methodologies',
        'Familiarity with quality management systems is a plus'
      ]
    },
    {
      id: 'qa-engineer',
      title: 'QA Engineer — Regulated Apps',
      department: 'Engineering',
      location: 'Remote / Hybrid',
      type: 'Full-time',
      responsibilities: [
        'Develop and execute automation and manual testing strategies',
        'Create and maintain test scripts and frameworks',
        'Perform release gating and validation activities',
        'Generate validation artifacts and documentation',
        'Collaborate with development team on quality improvements'
      ],
      requirements: [
        'ISTQB certification or practical experience with regulated validation',
        '3+ years experience in QA/Testing roles',
        'Experience with test automation frameworks (Selenium, Cypress, etc.)',
        'Understanding of FDA 21 CFR Part 11 requirements is desirable',
        'Strong attention to detail and documentation skills'
      ]
    },
    {
      id: 'senior-developer',
      title: 'Senior Full-Stack Developer',
      department: 'Engineering',
      location: 'Remote / Hybrid',
      type: 'Full-time',
      responsibilities: [
        'Design and develop scalable enterprise applications',
        'Implement features following GxP-compliant development practices',
        'Mentor junior developers and conduct code reviews',
        'Collaborate with product team on technical solutions',
        'Contribute to system architecture decisions'
      ],
      requirements: [
        '5+ years experience in full-stack development',
        'Strong proficiency in React, Node.js, and cloud technologies',
        'Experience with regulated software development is a plus',
        'Excellent problem-solving and communication skills',
        'Experience with CI/CD pipelines and DevOps practices'
      ]
    },
    {
      id: 'implementation-consultant',
      title: 'Implementation Consultant',
      department: 'Professional Services',
      location: 'Remote / On-site',
      type: 'Full-time',
      responsibilities: [
        'Lead client implementations and system configurations',
        'Conduct client training sessions and workshops',
        'Gather requirements and provide solution recommendations',
        'Create implementation documentation and validation protocols',
        'Provide post-go-live support and optimization guidance'
      ],
      requirements: [
        '4+ years experience in software implementation or consulting',
        'Background in life sciences, pharmaceutical, or regulated industries',
        'Strong project management and communication skills',
        'Willingness to travel to client sites as needed',
        'Experience with quality management systems preferred'
      ]
    }
  ];

  const toggleJob = (jobId: string) => {
    setExpandedJob(expandedJob === jobId ? null : jobId);
  };

  return (
    <div className="careers-page">
      {/* Hero */}
      <section className="careers-hero">
        <div className="container">
          <div className="careers-hero-content">
            <span className="badge">Join Our Team</span>
            <h1>Careers at Aurexa</h1>
            <p>
              Join a specialist team building regulated software that matters. 
              Expect collaborative teams, domain learning opportunities and real 
              ownership over quality-critical products.
            </p>
          </div>
        </div>
      </section>

      {/* Why Aurexa */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="badge">Why Work With Us</span>
            <h2>Build Software That Matters</h2>
            <p>
              At Aurexa, you'll work on products that directly impact quality and 
              compliance in regulated industries.
            </p>
          </div>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <span className="badge">Open Positions</span>
            <h2>Current Opportunities</h2>
            <p>
              Find your next role at Aurexa. We're always looking for talented 
              individuals to join our growing team.
            </p>
          </div>

          <div className="jobs-list">
            {jobs.map(job => (
              <div key={job.id} className={`job-card ${expandedJob === job.id ? 'expanded' : ''}`}>
                <button 
                  className="job-header"
                  onClick={() => toggleJob(job.id)}
                >
                  <div className="job-info">
                    <h3>{job.title}</h3>
                    <div className="job-meta">
                      <span><Briefcase size={16} /> {job.department}</span>
                      <span><MapPin size={16} /> {job.location}</span>
                      <span><Clock size={16} /> {job.type}</span>
                    </div>
                  </div>
                  <ChevronDown className="job-arrow" size={24} />
                </button>

                <div className="job-content">
                  <div className="job-section">
                    <h4>Responsibilities</h4>
                    <ul>
                      {job.responsibilities.map((item, idx) => (
                        <li key={idx}>
                          <CheckCircle size={16} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="job-section">
                    <h4>Requirements</h4>
                    <ul>
                      {job.requirements.map((item, idx) => (
                        <li key={idx}>
                          <CheckCircle size={16} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <a 
                    href={`mailto:careers@aurexatech.com?subject=Application for ${job.title}`}
                    className="btn btn-primary"
                  >
                    Apply Now <ArrowRight size={18} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="careers-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Don't See a Fit?</h2>
            <p>
              We're always interested in meeting talented people. Send us your resume 
              and we'll keep you in mind for future opportunities.
            </p>
            <a 
              href="mailto:careers@aurexatech.com?subject=General Application"
              className="btn btn-primary"
            >
              Send Your Resume <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
